import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { formatDateTimeEDT } from '../utils/formatters';

const SurveyCardContainer = styled.div`
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
    transform: translateY(-2px);
  }
`;

const SurveyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background-color: var(--card-header-bg);
  border-bottom: 1px solid var(--border-color);
`;

const SurveyTitle = styled.h3`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--primary-color);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const SurveyDate = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: normal;
`;

const SurveyBody = styled.div`
  padding: ${props => props.expanded ? '1.5rem' : '0'};
  max-height: ${props => props.expanded ? '2000px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 1.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const SummaryValue = styled.span`
  font-size: 0.875rem;
  color: var(--text-primary);
  font-weight: 500;
`;

const EffectivenessValue = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  span {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${props => {
      const rating = parseInt(props.rating, 10);
      if (rating >= 8) return 'var(--success)';
      if (rating >= 5) return 'var(--warning)';
      return 'var(--danger)';
    }};
  }
`;

const SurveyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1.5rem;
  background-color: var(--card-header-bg);
  border-top: ${props => props.expanded ? '1px solid var(--border-color)' : 'none'};
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.25rem 0;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s;
    transform: ${props => props.expanded ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

const ViewDetailsLink = styled(Link)`
  color: var(--primary-color);
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    text-decoration: underline;
  }
  
  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: ${props => {
    if (props.type === 'success') return 'var(--success-bg)';
    if (props.type === 'warning') return 'var(--warning-bg)';
    return 'var(--gray-100)';
  }};
  color: ${props => {
    if (props.type === 'success') return 'var(--success-color)';
    if (props.type === 'warning') return 'var(--warning-color)';
    return 'var(--text-primary)';
  }};
`;

const SurveyDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SurveySection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 0.75rem 0;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--border-color);
`;

const DetailItem = styled.div`
  margin-bottom: 0.75rem;
`;

const DetailLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-primary);
`;

const SurveyCard = ({ survey }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!survey) return null;
  
  // Format weight loss value
  const weightLoss = survey.weight_loss_in_last_30_days
    ? `${parseFloat(survey.weight_loss_in_last_30_days)} lbs`
    : 'Not reported';
    
  // Determine if there are side effects
  const hasSideEffects = survey.experienced_side_effects === 'Yes';
  
  return (
    <SurveyCardContainer>
      <SurveyHeader>
        <SurveyTitle>
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <path d="M14 2v6h6"></path>
            <path d="M16 13H8"></path>
            <path d="M16 17H8"></path>
            <path d="M10 9H8"></path>
          </svg>
          Survey #{survey.survey_id}
        </SurveyTitle>
        <SurveyDate>{formatDateTimeEDT(survey.created_at)}</SurveyDate>
      </SurveyHeader>
      
      <SummaryRow>
        <SummaryItem>
          <SummaryLabel>Current Medication</SummaryLabel>
          <SummaryValue>{survey.current_medication || 'Not specified'}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Current Dose</SummaryLabel>
          <SummaryValue>{survey.current_dose || 'Not specified'}</SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Effectiveness</SummaryLabel>
          <SummaryValue>
            <EffectivenessValue rating={survey.medication_effectiveness || 0}>
              <span></span>
              {survey.medication_effectiveness ? `${survey.medication_effectiveness}/10` : 'Not rated'}
            </EffectivenessValue>
          </SummaryValue>
        </SummaryItem>
        <SummaryItem>
          <SummaryLabel>Side Effects</SummaryLabel>
          <SummaryValue>
            <StatusBadge type={hasSideEffects ? 'warning' : 'success'}>
              {hasSideEffects ? 'Yes' : 'No'}
            </StatusBadge>
          </SummaryValue>
        </SummaryItem>
      </SummaryRow>
      
      <SurveyBody expanded={expanded}>
        <SurveyDetailsGrid>
          <SurveySection>
            <SectionTitle>Medication Details</SectionTitle>
            <DetailItem>
              <DetailLabel>Starting Dose</DetailLabel>
              <DetailValue>{survey.starting_dose || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Last Dose Increase</DetailLabel>
              <DetailValue>{survey.last_dose_increase || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Increasing Dosage</DetailLabel>
              <DetailValue>{survey.increasing_dosage || 'Not specified'}</DetailValue>
            </DetailItem>
            {hasSideEffects && (
              <>
                <DetailItem>
                  <DetailLabel>Side Effects Description</DetailLabel>
                  <DetailValue>{survey.side_effects || 'Not specified'}</DetailValue>
                </DetailItem>
                <DetailItem>
                  <DetailLabel>Interfering with Daily Life</DetailLabel>
                  <DetailValue>{survey.side_effect_interfering || 'Not specified'}</DetailValue>
                </DetailItem>
              </>
            )}
          </SurveySection>
          
          <SurveySection>
            <SectionTitle>Weight & Lifestyle Changes</SectionTitle>
            <DetailItem>
              <DetailLabel>Weight Loss Goal</DetailLabel>
              <DetailValue>{survey.weight_loss_goal || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Weight Loss in Last 30 Days</DetailLabel>
              <DetailValue>{weightLoss}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Problematic Weight Location</DetailLabel>
              <DetailValue>{survey.weight_location || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Diet Changes</DetailLabel>
              <DetailValue>{survey.diet_changes || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Exercise Changes</DetailLabel>
              <DetailValue>{survey.exercise_changes || 'Not specified'}</DetailValue>
            </DetailItem>
          </SurveySection>
          
          <SurveySection>
            <SectionTitle>Other Information</SectionTitle>
            <DetailItem>
              <DetailLabel>New Medical Conditions</DetailLabel>
              <DetailValue>{survey.new_medication_conditions || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>New Medications</DetailLabel>
              <DetailValue>{survey.new_medications || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Cravings Time</DetailLabel>
              <DetailValue>{survey.cravings_time || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Eating Habits</DetailLabel>
              <DetailValue>{survey.eating_habit || 'Not specified'}</DetailValue>
            </DetailItem>
            <DetailItem>
              <DetailLabel>Sleep Hours</DetailLabel>
              <DetailValue>{survey.sleep_hours || 'Not specified'}</DetailValue>
            </DetailItem>
          </SurveySection>
          
          <SurveySection>
            <SectionTitle>Address Information</SectionTitle>
            <DetailItem>
              <DetailLabel>Address Changed</DetailLabel>
              <DetailValue>{survey.address_changed || 'Not specified'}</DetailValue>
            </DetailItem>
            {survey.address_changed !== 'No, my address is the same' && (
              <DetailItem>
                <DetailLabel>New Shipping Address</DetailLabel>
                <DetailValue>{survey.new_shipping_address || 'Not specified'}</DetailValue>
              </DetailItem>
            )}
          </SurveySection>
        </SurveyDetailsGrid>
      </SurveyBody>
      
      <SurveyFooter expanded={expanded}>
        <ExpandButton 
          expanded={expanded} 
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? 'Collapse Details' : 'View More Details'}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </ExpandButton>
        <ViewDetailsLink to={`/surveys/${survey.survey_id}`}>
          Full Details
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </ViewDetailsLink>
      </SurveyFooter>
    </SurveyCardContainer>
  );
};

export default SurveyCard;